import React, { useEffect, useState } from "react";
import ExcelJS from "exceljs";

import config from "../../../config/config";
import { getAuthToken } from "../../../utils/token";

const LogsPage = () => {
  // =====================================================
  // STATE
  // =====================================================

  const [startDate, setStartDate] = useState("2026-09-01");
  const [endDate, setEndDate] = useState("2026-09-30");

  const [logs, setLogs] = useState([]);

  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const [error, setError] = useState("");

  const [selectedLog, setSelectedLog] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  // =====================================================
  // FETCH LOGS
  // =====================================================

  const fetchLogs = async () => {
    if (!startDate || !endDate) {
      setError("Please select start date and end date.");
      return;
    }

    if (startDate > endDate) {
      setError(
        "Start date cannot be greater than end date."
      );
      return;
    }

    try {
      setLoading(true);
      setError("");
      setCurrentPage(1);

      const response = await fetch(
        `${config.API}/admin/showlogs?start_date=${encodeURIComponent(
          startDate
        )}&end_date=${encodeURIComponent(endDate)}`,
        {
          method: "GET",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${getAuthToken()}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(
          `HTTP Error: ${response.status}`
        );
      }

      const data = await response.json();

      if (data.success) {
        setLogs(data.logs || []);
      } else {
        setLogs([]);
        setError(
          data.message || "Unable to fetch logs."
        );
      }
    } catch (err) {
      console.error("Fetch logs error:", err);

      setLogs([]);

      setError(
        err.message ||
          "Something went wrong while fetching logs."
      );
    } finally {
      setLoading(false);
    }
  };

  // =====================================================
  // INITIAL LOAD
  // =====================================================

  useEffect(() => {
    fetchLogs();
  }, []);

  // =====================================================
  // FORMAT DATE
  // =====================================================

  const formatDateTime = (dateString) => {
    if (!dateString) {
      return "-";
    }

    const date = new Date(dateString);

    if (Number.isNaN(date.getTime())) {
      return dateString;
    }

    return date.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  // =====================================================
  // METHOD BADGE
  // =====================================================

  const getMethodBadge = (method) => {
    const classes = {
      GET: "bg-success",
      POST: "bg-primary",
      PUT: "bg-warning text-dark",
      PATCH: "bg-info text-dark",
      DELETE: "bg-danger",
    };

    return (
      classes[
        method?.toUpperCase()
      ] || "bg-secondary"
    );
  };

  // =====================================================
  // PAGINATION
  // =====================================================

  const totalPages = Math.ceil(
    logs.length / perPage
  );

  const startIndex =
    (currentPage - 1) * perPage;

  const endIndex = Math.min(
    startIndex + perPage,
    logs.length
  );

  const currentLogs = logs.slice(
    startIndex,
    endIndex
  );

  // =====================================================
  // GO TO PAGE
  // =====================================================

  const goToPage = (page) => {
    if (page < 1 || page > totalPages) {
      return;
    }

    setCurrentPage(page);
  };

  // =====================================================
  // PAGINATION NUMBERS
  // =====================================================

  const getPaginationPages = () => {
    const pages = [];

    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }

      return pages;
    }

    pages.push(1);

    if (currentPage > 3) {
      pages.push("left-dots");
    }

    const start = Math.max(
      2,
      currentPage - 1
    );

    const end = Math.min(
      totalPages - 1,
      currentPage + 1
    );

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (currentPage < totalPages - 2) {
      pages.push("right-dots");
    }

    pages.push(totalPages);

    return pages;
  };

  // =====================================================
  // PAGE SIZE
  // =====================================================

  const handlePerPageChange = (e) => {
    setPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  // =====================================================
  // EXCEL EXPORT
  // =====================================================

  const exportToExcel = async () => {
    if (!logs || logs.length === 0) {
      setError("No logs available to export.");
      return;
    }

    try {
      setExporting(true);
      setError("");

      const workbook = new ExcelJS.Workbook();

      workbook.creator = "System Logs";
      workbook.lastModifiedBy = "System Logs";
      workbook.created = new Date();
      workbook.modified = new Date();

      const worksheet =
        workbook.addWorksheet("API Logs");

      // -------------------------------------------------
      // Worksheet columns
      // -------------------------------------------------

      worksheet.columns = [
        {
          header: "Log ID",
          key: "id",
          width: 12,
        },
        {
          header: "User ID",
          key: "user_id",
          width: 15,
        },
        {
          header: "Method",
          key: "method",
          width: 12,
        },
        {
          header: "URL",
          key: "url",
          width: 65,
        },
        {
          header: "IP Address",
          key: "ip_address",
          width: 18,
        },
        {
          header: "Created At",
          key: "created_at",
          width: 25,
        },
        {
          header: "Updated At",
          key: "updated_at",
          width: 25,
        },
        {
          header: "Payload",
          key: "payload",
          width: 70,
        },
      ];

      // -------------------------------------------------
      // Header styling
      // -------------------------------------------------

      const headerRow =
        worksheet.getRow(1);

      headerRow.height = 28;

      headerRow.font = {
        bold: true,
        color: {
          argb: "FFFFFFFF",
        },
        size: 11,
      };

      headerRow.alignment = {
        horizontal: "center",
        vertical: "middle",
      };

      headerRow.eachCell((cell) => {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: {
            argb: "FF0D6EFD",
          },
        };

        cell.border = {
          top: {
            style: "thin",
            color: {
              argb: "FFFFFFFF",
            },
          },
          left: {
            style: "thin",
            color: {
              argb: "FFFFFFFF",
            },
          },
          bottom: {
            style: "thin",
            color: {
              argb: "FFFFFFFF",
            },
          },
          right: {
            style: "thin",
            color: {
              argb: "FFFFFFFF",
            },
          },
        };
      });

      // -------------------------------------------------
      // Add log data
      // -------------------------------------------------

      logs.forEach((log) => {
        const row = worksheet.addRow({
          id: log.id ?? "",

          user_id:
            log.user_id || "Guest",

          method:
            log.method || "",

          url:
            log.url || "",

          ip_address:
            log.ip_address || "",

          created_at:
            formatDateTime(
              log.created_at
            ),

          updated_at:
            formatDateTime(
              log.updated_at
            ),

          payload: JSON.stringify(
            log.payload || {},
            null,
            2
          ),
        });

        row.alignment = {
          vertical: "top",
          wrapText: true,
        };

        row.height = 45;

        row.eachCell((cell) => {
          cell.border = {
            top: {
              style: "thin",
              color: {
                argb: "FFE0E0E0",
              },
            },
            left: {
              style: "thin",
              color: {
                argb: "FFE0E0E0",
              },
            },
            bottom: {
              style: "thin",
              color: {
                argb: "FFE0E0E0",
              },
            },
            right: {
              style: "thin",
              color: {
                argb: "FFE0E0E0",
              },
            },
          };
        });

        // Method formatting

        const method =
          log.method?.toUpperCase();

        const methodCell =
          row.getCell("method");

        if (method === "GET") {
          methodCell.font = {
            bold: true,
            color: {
              argb: "FF198754",
            },
          };
        }

        if (method === "POST") {
          methodCell.font = {
            bold: true,
            color: {
              argb: "FF0D6EFD",
            },
          };
        }

        if (method === "PUT") {
          methodCell.font = {
            bold: true,
            color: {
              argb: "FFFF8C00",
            },
          };
        }

        if (method === "PATCH") {
          methodCell.font = {
            bold: true,
            color: {
              argb: "FF0DCAF0",
            },
          };
        }

        if (method === "DELETE") {
          methodCell.font = {
            bold: true,
            color: {
              argb: "FFDC3545",
            },
          };
        }
      });

      // -------------------------------------------------
      // Freeze first row
      // -------------------------------------------------

      worksheet.views = [
        {
          state: "frozen",
          ySplit: 1,
        },
      ];

      // -------------------------------------------------
      // Auto filter
      // -------------------------------------------------

      worksheet.autoFilter = {
        from: "A1",
        to: `H${logs.length + 1}`,
      };

      // -------------------------------------------------
      // Add summary information
      // -------------------------------------------------

      worksheet.headerFooter.oddFooter =
        `Page &P of &N`;

      // -------------------------------------------------
      // Generate Excel file
      // -------------------------------------------------

      const buffer =
        await workbook.xlsx.writeBuffer();

      const blob = new Blob(
        [buffer],
        {
          type:
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }
      );

      const downloadUrl =
        window.URL.createObjectURL(blob);

      const link =
        document.createElement("a");

      link.href = downloadUrl;

      link.download =
        `api_logs_${startDate}_to_${endDate}.xlsx`;

      document.body.appendChild(link);

      link.click();

      document.body.removeChild(link);

      window.URL.revokeObjectURL(
        downloadUrl
      );
    } catch (err) {
      console.error(
        "Excel export error:",
        err
      );

      setError(
        "Failed to export logs to Excel."
      );
    } finally {
      setExporting(false);
    }
  };

  // =====================================================
  // RENDER
  // =====================================================

  return (
    <div className="container-fluid py-4">

      {/* =================================================
          PAGE HEADER
      ================================================= */}

      <div className="d-flex justify-content-between align-items-center mb-4">

        <div>
          <h2 className="mb-1 fw-bold">
            <i className="bi bi-journal-text me-2 text-primary"></i>
            System Logs
          </h2>

          <p className="text-muted mb-0">
            View and monitor API activity logs
          </p>
        </div>

        <div className="card shadow-sm border-0">

          <div className="card-body py-2 px-4 text-center">

            <small className="text-muted d-block">
              Total Logs
            </small>

            <h4 className="mb-0 fw-bold">
              {logs.length}
            </h4>

          </div>

        </div>

      </div>


      {/* =================================================
          FILTER CARD
      ================================================= */}

      <div className="card shadow-sm border-0 mb-4">

        <div className="card-body">

          <div className="row g-3 align-items-end">

            <div className="col-lg-3 col-md-4">

              <label className="form-label fw-semibold">
                Start Date
              </label>

              <input
                type="date"
                className="form-control"
                value={startDate}
                onChange={(e) =>
                  setStartDate(
                    e.target.value
                  )
                }
              />

            </div>


            <div className="col-lg-3 col-md-4">

              <label className="form-label fw-semibold">
                End Date
              </label>

              <input
                type="date"
                className="form-control"
                value={endDate}
                onChange={(e) =>
                  setEndDate(
                    e.target.value
                  )
                }
              />

            </div>


            <div className="col-lg-2 col-md-4">

              <button
                type="button"
                className="btn btn-primary w-100"
                onClick={fetchLogs}
                disabled={loading}
              >

                {loading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                    ></span>

                    Loading...
                  </>
                ) : (
                  <>
                    <i className="bi bi-search me-2"></i>
                    Search
                  </>
                )}

              </button>

            </div>


            <div className="col-lg-2 col-md-4">

              <button
                type="button"
                className="btn btn-outline-secondary w-100"
                onClick={fetchLogs}
                disabled={loading}
              >

                <i className="bi bi-arrow-clockwise me-2"></i>

                Refresh

              </button>

            </div>

          </div>

        </div>

      </div>


      {/* =================================================
          ERROR
      ================================================= */}

      {error && (

        <div
          className="alert alert-danger alert-dismissible fade show"
          role="alert"
        >

          <i className="bi bi-exclamation-triangle me-2"></i>

          {error}

          <button
            type="button"
            className="btn-close"
            onClick={() =>
              setError("")
            }
          ></button>

        </div>

      )}


      {/* =================================================
          TABLE CARD
      ================================================= */}

      <div className="card shadow-sm border-0">

        {/* Table Header */}

        <div className="card-header bg-white py-3">

          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">

            <div>

              <h5 className="mb-1 fw-semibold">

                <i className="bi bi-activity me-2 text-primary"></i>

                API Activity

              </h5>

              <small className="text-muted">

                {startDate} → {endDate}

              </small>

            </div>


            {/* Actions */}

            <div className="d-flex align-items-center gap-2">

              <span className="badge bg-primary rounded-pill px-3 py-2">

                {logs.length} Records

              </span>


              <button
                type="button"
                className="btn btn-success btn-sm"
                onClick={exportToExcel}
                disabled={
                  loading ||
                  exporting ||
                  logs.length === 0
                }
              >

                {exporting ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-1"
                      role="status"
                    ></span>

                    Exporting...
                  </>
                ) : (
                  <>
                    <i className="bi bi-file-earmark-excel me-1"></i>

                    Export Excel
                  </>
                )}

              </button>

            </div>

          </div>

        </div>


        {/* =================================================
            TABLE BODY
        ================================================= */}

        <div className="card-body p-0">

          {loading ? (

            <div className="text-center py-5">

              <div
                className="spinner-border text-primary"
                role="status"
              ></div>

              <p className="text-muted mt-3 mb-0">
                Loading logs...
              </p>

            </div>

          ) : logs.length === 0 ? (

            <div className="text-center py-5">

              <i className="bi bi-inbox fs-1 text-muted"></i>

              <h5 className="mt-3">
                No logs found
              </h5>

              <p className="text-muted mb-0">
                No activity logs were found
                for the selected date range.
              </p>

            </div>

          ) : (

            <div className="table-responsive">

              <table className="table table-hover table-bordered align-middle mb-0">

                <thead className="table-light">

                  <tr>

                    <th className="text-center">
                      #
                    </th>

                    <th>
                      ID
                    </th>

                    <th>
                      User
                    </th>

                    <th>
                      Method
                    </th>

                    <th>
                      URL
                    </th>

                    <th>
                      IP Address
                    </th>

                    <th>
                      Created At
                    </th>

                    <th className="text-center">
                      Action
                    </th>

                  </tr>

                </thead>


                <tbody>

                  {currentLogs.map(
                    (log, index) => (

                      <tr key={log.id}>

                        <td className="text-center text-muted">

                          {startIndex +
                            index +
                            1}

                        </td>


                        <td>

                          <span className="fw-semibold">
                            #{log.id}
                          </span>

                        </td>


                        <td>

                          {log.user_id ? (

                            <span className="badge bg-secondary">

                              <i className="bi bi-person me-1"></i>

                              User{" "}
                              {log.user_id}

                            </span>

                          ) : (

                            <span className="badge bg-light text-dark border">

                              <i className="bi bi-person-x me-1"></i>

                              Guest

                            </span>

                          )}

                        </td>


                        <td>

                          <span
                            className={`badge ${getMethodBadge(
                              log.method
                            )}`}
                          >
                            {log.method}
                          </span>

                        </td>


                        <td>

                          <code
                            className="text-dark"
                            style={{
                              fontSize:
                                "12px",
                              wordBreak:
                                "break-all",
                            }}
                          >

                            {log.url}

                          </code>

                        </td>


                        <td>

                          <code>
                            {log.ip_address}
                          </code>

                        </td>


                        <td className="text-nowrap">

                          <small>
                            {formatDateTime(
                              log.created_at
                            )}
                          </small>

                        </td>


                        <td className="text-center">

                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={() =>
                              setSelectedLog(
                                log
                              )
                            }
                          >

                            <i className="bi bi-eye me-1"></i>

                            View

                          </button>

                        </td>

                      </tr>

                    )
                  )}

                </tbody>

              </table>

            </div>

          )}

        </div>


        {/* =================================================
            PAGINATION
        ================================================= */}

        {!loading &&
          logs.length > 0 && (

            <div className="card-footer bg-white py-3">

              <div className="row align-items-center g-3">

                {/* Information */}

                <div className="col-lg-4 col-md-5">

                  <div className="d-flex align-items-center gap-3">

                    <div className="text-muted small text-nowrap">

                      Showing{" "}

                      <strong className="text-dark">
                        {startIndex + 1}
                      </strong>

                      {" "}to{" "}

                      <strong className="text-dark">
                        {endIndex}
                      </strong>

                      {" "}of{" "}

                      <strong className="text-dark">
                        {logs.length}
                      </strong>

                    </div>


                    <select
                      className="form-select form-select-sm"
                      style={{
                        width: "80px",
                      }}
                      value={perPage}
                      onChange={
                        handlePerPageChange
                      }
                      aria-label="Rows per page"
                    >

                      <option value={10}>
                        10
                      </option>

                      <option value={25}>
                        25
                      </option>

                      <option value={50}>
                        50
                      </option>

                      <option value={100}>
                        100
                      </option>

                    </select>

                  </div>

                </div>


                {/* Pagination */}

                <div className="col-lg-8 col-md-7">

                  <div className="d-flex justify-content-md-end justify-content-center">

                    <nav aria-label="Logs pagination">

                      <ul className="pagination mb-0 shadow-sm">


                        {/* First */}

                        <li
                          className={`page-item ${
                            currentPage === 1
                              ? "disabled"
                              : ""
                          }`}
                        >

                          <button
                            type="button"
                            className="page-link"
                            onClick={() =>
                              goToPage(1)
                            }
                            disabled={
                              currentPage === 1
                            }
                            title="First page"
                          >

                            <i className="bi bi-chevron-double-left"></i>

                          </button>

                        </li>


                        {/* Previous */}

                        <li
                          className={`page-item ${
                            currentPage === 1
                              ? "disabled"
                              : ""
                          }`}
                        >

                          <button
                            type="button"
                            className="page-link px-3"
                            onClick={() =>
                              goToPage(
                                currentPage - 1
                              )
                            }
                            disabled={
                              currentPage === 1
                            }
                          >

                            <i className="bi bi-chevron-left me-1"></i>

                            Previous

                          </button>

                        </li>


                        {/* Page Numbers */}

                        {getPaginationPages().map(
                          (page, index) => {

                            const isDots =
                              page ===
                                "left-dots" ||
                              page ===
                                "right-dots";

                            if (isDots) {
                              return (
                                <li
                                  key={`${page}-${index}`}
                                  className="page-item disabled"
                                >

                                  <span className="page-link">
                                    ...
                                  </span>

                                </li>
                              );
                            }

                            return (
                              <li
                                key={page}
                                className={`page-item ${
                                  currentPage ===
                                  page
                                    ? "active"
                                    : ""
                                }`}
                              >

                                <button
                                  type="button"
                                  className="page-link"
                                  onClick={() =>
                                    goToPage(
                                      page
                                    )
                                  }
                                >

                                  {page}

                                </button>

                              </li>
                            );
                          }
                        )}


                        {/* Next */}

                        <li
                          className={`page-item ${
                            currentPage ===
                            totalPages
                              ? "disabled"
                              : ""
                          }`}
                        >

                          <button
                            type="button"
                            className="page-link px-3"
                            onClick={() =>
                              goToPage(
                                currentPage + 1
                              )
                            }
                            disabled={
                              currentPage ===
                              totalPages
                            }
                          >

                            Next

                            <i className="bi bi-chevron-right ms-1"></i>

                          </button>

                        </li>


                        {/* Last */}

                        <li
                          className={`page-item ${
                            currentPage ===
                            totalPages
                              ? "disabled"
                              : ""
                          }`}
                        >

                          <button
                            type="button"
                            className="page-link"
                            onClick={() =>
                              goToPage(
                                totalPages
                              )
                            }
                            disabled={
                              currentPage ===
                              totalPages
                            }
                            title="Last page"
                          >

                            <i className="bi bi-chevron-double-right"></i>

                          </button>

                        </li>

                      </ul>

                    </nav>

                  </div>

                </div>

              </div>

            </div>

          )}

      </div>


      {/* =================================================
          LOG DETAILS MODAL
      ================================================= */}

      {selectedLog && (

        <div
          className="modal fade show d-block"
          tabIndex="-1"
          role="dialog"
          style={{
            backgroundColor:
              "rgba(0, 0, 0, 0.5)",
          }}
          onClick={() =>
            setSelectedLog(null)
          }
        >

          <div
            className="modal-dialog modal-xl modal-dialog-scrollable"
            role="document"
            onClick={(e) =>
              e.stopPropagation()
            }
          >

            <div className="modal-content">


              {/* Modal Header */}

              <div className="modal-header">

                <div>

                  <h5 className="modal-title">

                    <i className="bi bi-file-text me-2 text-primary"></i>

                    Log Details

                  </h5>

                  <small className="text-muted">

                    Log #{selectedLog.id}

                  </small>

                </div>


                <button
                  type="button"
                  className="btn-close"
                  onClick={() =>
                    setSelectedLog(
                      null
                    )
                  }
                ></button>

              </div>


              {/* Modal Body */}

              <div className="modal-body">

                {/* Request Information */}

                <h6 className="border-bottom pb-2 mb-3 fw-bold">

                  Request Information

                </h6>


                <div className="row g-3 mb-4">

                  <div className="col-md-3">

                    <div className="bg-light rounded p-3">

                      <small className="text-muted d-block mb-1">
                        Log ID
                      </small>

                      <strong>
                        #{selectedLog.id}
                      </strong>

                    </div>

                  </div>


                  <div className="col-md-3">

                    <div className="bg-light rounded p-3">

                      <small className="text-muted d-block mb-1">
                        User ID
                      </small>

                      <strong>
                        {selectedLog.user_id ||
                          "Guest"}
                      </strong>

                    </div>

                  </div>


                  <div className="col-md-3">

                    <div className="bg-light rounded p-3">

                      <small className="text-muted d-block mb-1">
                        HTTP Method
                      </small>

                      <span
                        className={`badge ${getMethodBadge(
                          selectedLog.method
                        )}`}
                      >
                        {selectedLog.method}
                      </span>

                    </div>

                  </div>


                  <div className="col-md-3">

                    <div className="bg-light rounded p-3">

                      <small className="text-muted d-block mb-1">
                        IP Address
                      </small>

                      <code>
                        {selectedLog.ip_address}
                      </code>

                    </div>

                  </div>


                  <div className="col-12">

                    <div className="bg-light rounded p-3">

                      <small className="text-muted d-block mb-1">
                        API URL
                      </small>

                      <code
                        className="text-dark"
                        style={{
                          wordBreak:
                            "break-all",
                        }}
                      >
                        {selectedLog.url}
                      </code>

                    </div>

                  </div>

                </div>


                {/* Timestamps */}

                <h6 className="border-bottom pb-2 mb-3 fw-bold">

                  Timestamps

                </h6>


                <div className="row g-3 mb-4">

                  <div className="col-md-6">

                    <div className="alert alert-light border mb-0">

                      <strong>
                        Created At
                      </strong>

                      <div className="mt-1">
                        {formatDateTime(
                          selectedLog.created_at
                        )}
                      </div>

                    </div>

                  </div>


                  <div className="col-md-6">

                    <div className="alert alert-light border mb-0">

                      <strong>
                        Updated At
                      </strong>

                      <div className="mt-1">
                        {formatDateTime(
                          selectedLog.updated_at
                        )}
                      </div>

                    </div>

                  </div>

                </div>


                {/* Payload */}

                <h6 className="border-bottom pb-2 mb-3 fw-bold">

                  Request Payload

                </h6>


                <div className="card bg-dark text-light">

                  <div className="card-body">

                    <pre
                      className="mb-0"
                      style={{
                        whiteSpace:
                          "pre-wrap",
                        wordBreak:
                          "break-word",
                        fontSize:
                          "13px",
                      }}
                    >
                      {JSON.stringify(
                        selectedLog.payload ||
                          {},
                        null,
                        2
                      )}
                    </pre>

                  </div>

                </div>

              </div>


              {/* Modal Footer */}

              <div className="modal-footer">

                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() =>
                    setSelectedLog(
                      null
                    )
                  }
                >
                  Close
                </button>

              </div>

            </div>

          </div>

        </div>

      )}

    </div>
  );
};

export default LogsPage;